import { useRef, useState, useEffect, useMemo, useCallback } from "react";

export const useWebSocketHook = (url, methods) => {
  const webSocketRef = useRef(null);
  const [messages, setMessages] = useState([]);

  // Memoize methods object to ensure stable reference
  const memoizedMethods = useMemo(() => {
    return methods || {};
  }, [methods]);

  // Memoize sendData function to prevent recreation on every render
  const sendData = useCallback((data) => {
    if (webSocketRef.current?.readyState === WebSocket.OPEN) {
      webSocketRef.current.send(JSON.stringify(data));
    } else {
      console.error("WebSocket is not open");
    }
  }, []);

  // WebSocket connection management
  useEffect(() => {
    if (!url) return;

    const ws = new WebSocket(url);
    console.log("WebSocket initialized:", url);

    ws.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);
        console.log("Received WebSocket message:", message);
        setMessages((prevMessages) => [...prevMessages, message]);
      } catch (error) {
        console.error("Error parsing WebSocket message:", error);
      }
    };

    ws.onerror = (error) => {
      console.error("WebSocket error:", error);
    };

    ws.onopen = () => {
      console.log("WebSocket connection opened");
    };

    ws.onclose = (event) => {
      console.log("WebSocket closed:", event.code, event.reason);
    };

    webSocketRef.current = ws;

    // Cleanup function
    return () => {
      console.log("Cleaning up WebSocket connection");
      if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
        ws.close(1000, "Component unmounting");
      }
      webSocketRef.current = null;
    };
  }, [url]);

  // Message processing with proper dependencies
  useEffect(() => {
    if (messages.length > 0) {
      // Process the latest message (last in array)
      const latestMessage = messages[messages.length - 1];
      console.log("Processing message:", latestMessage);
      
      if (latestMessage?.event && memoizedMethods[latestMessage.event]) {
        try {
          memoizedMethods[latestMessage.event]({ 
            message: latestMessage, 
            send: sendData 
          });
        } catch (error) {
          console.error("Error processing message event:", error);
        }
      }

      // Clear processed message to prevent reprocessing
      setMessages((prevMessages) => prevMessages.slice(0, -1));
    }
  }, [messages, memoizedMethods, sendData]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (webSocketRef.current) {
        webSocketRef.current.close(1000, "Hook cleanup");
        webSocketRef.current = null;
      }
    };
  }, []);

  return { webSocketSend: sendData };
};
